import request from '@/axios'
import axios from 'axios'
import xbb from '@xbb/xbb-utils'
import { ElMessage } from 'element-plus'
import utils, { isMobile } from '@/utils/index.js'

const { WEBPACK_NEW_GATEWAY_URL, WEBPACK_NEW_GATEWAY_URL_INDEPENDENT } = process.env

function getCredential(key) {
  const value = utils.xbbLocalStorageGet(key)
  if (value) return value
  const backup = utils.xbbLocalStorageGet(`ai_${key}`)
  if (backup) utils.xbbLocalStorageSet(key, backup)
  return backup
}

function utf8Bytes(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value)
  const binary = unescape(encodeURIComponent(value))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index)
  return bytes
}

async function sha256HexBytes(bytes) {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  }
  // 局域网 HTTP 环境不提供 crypto.subtle
  const cryptoJsModule = await import('crypto-js')
  const CryptoJS = cryptoJsModule.default || cryptoJsModule
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  const wordArray = CryptoJS.lib.WordArray.create(view)
  return CryptoJS.SHA256(wordArray).toString(CryptoJS.enc.Hex)
}

function appendV1Field(payload, value) {
  if (value === null || value === undefined) return `${payload}-1:`
  const text = String(value)
  return `${payload}${utf8Bytes(text).length}:${text}`
}

async function buildSkillMultipartSign({
  operation,
  corpid,
  userId,
  platform,
  skillId,
  displayTitle,
  files,
  token
}) {
  let payload = 'tenant-skill-multipart-v1\n'
  payload = appendV1Field(payload, operation)
  payload = appendV1Field(payload, corpid)
  payload = appendV1Field(payload, userId)
  payload = appendV1Field(payload, platform)
  payload = appendV1Field(payload, skillId)
  payload = appendV1Field(payload, displayTitle)
  payload += `${files.length}:`
  for (const { file, path } of files) {
    payload = appendV1Field(payload, path)
    payload = appendV1Field(payload, await sha256HexBytes(await file.arrayBuffer()))
  }
  return sha256HexBytes(utf8Bytes(payload + token))
}

async function sendSkillMultipart(url, operation, data, skillId = null) {
  const corpid = getCredential('corpid') || ''
  const userId = getCredential('userId') || ''
  const platform = isMobile() ? 'dingtalk' : 'web'
  const token = getCredential('xbbAccessToken') || '1'
  const displayTitle = data.displayTitle ?? null
  const files = Array.isArray(data.files) ? data.files : []
  const normalizedFiles = files.map(({ file, path }) => ({ file, path: path || file.name }))
  const sign = await buildSkillMultipartSign({
    operation,
    corpid,
    userId,
    platform,
    skillId,
    displayTitle,
    files: normalizedFiles,
    token
  })
  const formData = new FormData()
  formData.append('corpid', corpid)
  formData.append('userId', userId)
  formData.append('platform', platform)
  if (displayTitle !== null) formData.append('displayTitle', displayTitle)
  if (operation === 'update' && skillId != null) formData.append('skillId', String(skillId))
  normalizedFiles.forEach(({ file, path }) => formData.append('files', file, path))

  const baseURL =
    !xbb.isDevEnv() && utils.getPlatform(corpid) !== 'dd'
      ? WEBPACK_NEW_GATEWAY_URL_INDEPENDENT
      : WEBPACK_NEW_GATEWAY_URL
  const requestId = xbb.getGuid().replace(/-/g, '').substring(0, 16)
  try {
    const { data: response } = await axios.post(url, formData, {
      baseURL,
      timeout: 120000,
      headers: { sign, 'X-Request-ID': requestId }
    })
    if (response?.code !== 1) {
      if (response?.msg) ElMessage.error(response.msg)
      throw response
    }
    return response
  } catch (error) {
    if (error?.response) ElMessage.error(error.response.data?.msg || '技能上传请求失败')
    else if (error?.isAxiosError) ElMessage.error(error.message || '技能上传请求失败')
    throw error
  }
}

export function getSkillList(data) {
  return request({ url: '/api/qca/tenant/skills/list', data })
}

export function getSkillDetail(data) {
  return request({ url: '/api/qca/tenant/skills/get', data })
}

export function installSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/install`, data: {} })
}

export function enableSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/enable`, data: {} })
}

export function disableSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/disable`, data: {} })
}

export function getSkillShareTargets(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/share-targets`, data: {} })
}

export function setSkillShareEnabled(localSkillId, enabled) {
  return request({
    url: `/api/qca/tenant/skills/${localSkillId}/share-enabled`,
    data: { enabled }
  })
}

export function addSkillShareTarget(data) {
  const { localSkillId, ...payload } = data
  return request({
    url: `/api/qca/tenant/skills/${localSkillId}/share`,
    data: payload
  })
}

export function uninstallSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/uninstall`, data: {} })
}

export function deleteSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}`, method: 'delete', data: {} })
}

export function upgradeSkill(localSkillId) {
  return request({ url: `/api/qca/tenant/skills/${localSkillId}/upgrade-latest`, data: {} })
}

export function downloadSkill(localSkillId) {
  return request({
    url: `/api/qca/tenant/skills/${localSkillId}/download`,
    data: {},
    responseType: 'arraybuffer',
    transformResponse: [
      (data, headers) => {
        const type = headers.get('content-type') || ''
        const bytes = new Uint8Array(data)
        if (
          type.includes('json') ||
          new TextDecoder().decode(bytes.slice(0, 64)).trimStart().startsWith('{')
        ) {
          try {
            return JSON.parse(new TextDecoder().decode(data))
          } catch {
            return { code: 0, msg: '下载响应格式错误' }
          }
        }
        if (!bytes.length || type.includes('text/html')) {
          return { code: 0, msg: '下载响应格式错误' }
        }
        return {
          code: 1,
          result: {
            blob: new Blob([data], { type: type || 'application/octet-stream' }),
            disposition: headers.get('content-disposition') || ''
          }
        }
      }
    ]
  })
}

export function createSkillVersion(localSkillId, data) {
  return sendSkillMultipart(
    `/api/qca/tenant/skills/${localSkillId}/versions`,
    'versions',
    data,
    localSkillId
  )
}

export function uploadSkill(data) {
  return sendSkillMultipart('/api/qca/tenant/skills/upload', 'upload', data)
}

export function updateSkillMultipart(data) {
  return sendSkillMultipart('/api/qca/tenant/skills/update', 'update', data, data.skillId)
}
